import { useEffect, useMemo, useState } from "react";
import { Pencil, ShieldCheck, Upload } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { Spinner } from "@/components/ui/Spinner";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useClients } from "@/features/clients/hooks/useClients";
import {
  EmptyState, PageHeader, SectionCard, TableShell, Th,
} from "../../components/ui";
import { InfoHint } from "../../components/wnioski/InfoHint";
import {
  useClientBranches, usePoaClientConfig, usePoaConfigs, usePoaMutations, useSelectableOffices,
  usePoaOfficeConfig, usePoaProjects, usePowersOfAttorney,
} from "../../hooks";
import type { PowerOfAttorneyRow } from "../../types";
import { fmtDate } from "../../utils";

const VALIDITY_BADGE: Record<string, { color: "green" | "yellow" | "red" | "gray"; label: string }> = {
  active: { color: "green", label: "Aktywne" },
  expiring: { color: "yellow", label: "Wygasa wkrótce" },
  expired: { color: "red", label: "Wygasłe" },
  inactive: { color: "gray", label: "Nieaktywne" },
};

type PoaValidity = PowerOfAttorneyRow["validity"];
const STATUS_KEYS: PoaValidity[] = ["active", "expiring", "expired", "inactive"];
// Sort order for the "status" column (ascending = most urgent first).
const STATUS_RANK: Record<PoaValidity, number> = { expired: 0, expiring: 1, active: 2, inactive: 3 };

const DEFAULT_FORM = {
  principal_kind: "client" as "client" | "elektim",
  principal_client: "",
  principal_label: "",
  attorney_mode: "user" as "user" | "external",
  attorney_user: "",
  attorney_name: "",
  number: "",
  scope: "",
  eop_client: "",
  eop_branch: "",
  project: "",
  valid_from: "",
  valid_until: "",
  notes: "",
  is_active: true,
};

/**
 * Rejestr pełnomocnictw: mocodawca (klient/ELEKTIM) → pełnomocnik (pracownik albo
 * osoba spoza systemu, np. pracownik klienta), skan, ważność. Silnik wniosków
 * auto-podpina stąd ważne PoA dla osoby odpowiedzialnej; tu też konfiguruje się
 * urząd opłaty skarbowej mocodawcy oraz praktykę urzędu-adresata (czy wymaga
 * drugiego pełnomocnictwa — łańcuch zależy od urzędu, nie od inwestora).
 */
export function PoaRegistryPage() {
  const { data: poas, isLoading } = usePowersOfAttorney();
  const m = usePoaMutations();
  const { data: users } = useInternalUsers();
  const { data: clients } = useClients();
  const { data: offices } = useSelectableOffices();
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterPrincipal, setFilterPrincipal] = useState("");
  const [filterAttorney, setFilterAttorney] = useState("");
  const [filterStatus, setFilterStatus] = useState<PoaValidity | "">("");
  const [sort, setSort] = useState<{ key: "validity" | "status"; dir: "asc" | "desc" } | null>(null);
  const [configClient, setConfigClient] = useState("");
  const [configOffice, setConfigOffice] = useState("");
  const [chainOffice, setChainOffice] = useState("");
  const [chainSecond, setChainSecond] = useState(false);

  // Prefill saved configs so saving one field never silently resets the other.
  const clientConfig = usePoaClientConfig(configClient || undefined);
  useEffect(() => {
    if (clientConfig.data) setConfigOffice(clientConfig.data.stamp_duty_office_id ?? "");
  }, [clientConfig.data]);
  const officeConfig = usePoaOfficeConfig(chainOffice || undefined);
  useEffect(() => {
    if (officeConfig.data) setChainSecond(officeConfig.data.requires_second_poa);
  }, [officeConfig.data]);
  const { data: savedConfigs } = usePoaConfigs();

  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [file, setFile] = useState<File | null>(null);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  // Optional scoping pickers (all three fields independent, all optional).
  const { data: poaProjects } = usePoaProjects();
  // Oddziały pokazujemy dla mocodawcy, gdy „Klient (oddziału)” został pusty — inaczej
  // pole „Oddział” było zablokowane i wpisy lądowały w rejestrze bez oddziału, przez co
  // automat traktował je jako uniwersalne i podpinał do wniosków każdego oddziału.
  const branchClientId = form.eop_client || (form.principal_kind === "client" ? form.principal_client : "");
  const { data: eopBranches } = useClientBranches(branchClientId || undefined);

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFile(null);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...DEFAULT_FORM });
    setFile(null);
    setFormOpen(true);
  };

  const openEdit = (p: PowerOfAttorneyRow) => {
    setEditingId(p.id);
    setForm({
      principal_kind: p.principal_kind,
      principal_client: p.principal_client ?? "",
      principal_label: p.principal_label,
      attorney_mode: p.attorney_user ? "user" : "external",
      attorney_user: p.attorney_user ?? "",
      attorney_name: p.attorney_name,
      number: p.number,
      scope: p.scope,
      eop_client: p.eop_client ?? "",
      eop_branch: p.eop_branch ?? "",
      project: p.project ?? "",
      valid_from: p.valid_from ?? "",
      valid_until: p.valid_until ?? "",
      notes: p.notes,
      is_active: p.is_active,
    });
    setFile(null);
    setFormOpen(true);
  };

  const rows = useMemo(() => {
    let out = poas ?? [];
    const p = filterPrincipal.trim().toLowerCase();
    const a = filterAttorney.trim().toLowerCase();
    if (p) out = out.filter((r) => r.principal_display.toLowerCase().includes(p));
    if (a) out = out.filter((r) => r.attorney_display.toLowerCase().includes(a));
    if (filterStatus) out = out.filter((r) => r.validity === filterStatus);
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((x, y) => {
        let cmp: number;
        if (sort.key === "validity") {
          // Sort by expiry; bezterminowe (null) = never expires → treated as latest.
          const vx = x.valid_until ? Date.parse(x.valid_until) : Infinity;
          const vy = y.valid_until ? Date.parse(y.valid_until) : Infinity;
          cmp = vx === vy ? 0 : vx - vy;
        } else {
          cmp = STATUS_RANK[x.validity] - STATUS_RANK[y.validity];
        }
        return cmp * dir;
      });
    }
    return out;
  }, [poas, filterPrincipal, filterAttorney, filterStatus, sort]);

  const hasFilters = !!(filterPrincipal || filterAttorney || filterStatus);
  const toggleSort = (key: "validity" | "status") =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortArrow = (key: "validity" | "status") =>
    (sort?.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const editingRow = useMemo(
    () => (poas ?? []).find((p) => p.id === editingId) ?? null,
    [poas, editingId],
  );

  const userOptions = (users ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username }));
  const clientOptions = (clients ?? []).map((c) => ({ value: c.id, label: c.name }));
  // TAURON numeruje pełnomocnictwa per zlecenie i drukuje numer na porozumieniu — dlatego
  // przy tym mocodawcy numer i przedsięwzięcie są obowiązkowe (backend waliduje to samo).
  const formClient = (clients ?? []).find((c) => c.id === (form.principal_client || form.eop_client));
  const isTauron = (formClient?.operator || "") === "tauron"
    || (!formClient?.operator && /tauron/i.test(formClient?.name ?? ""));
  const projectOptions = (poaProjects ?? []).map((p) => ({
    value: p.id, label: p.client_name ? `${p.name} — ${p.client_name}` : p.name,
  }));
  const branchOptions = (eopBranches ?? []).map((b) => ({ value: b.id, label: b.display_name }));

  const submit = () => {
    const payload = {
      principal_kind: form.principal_kind,
      principal_client: form.principal_kind === "client" ? form.principal_client || null : null,
      principal_label: form.principal_label,
      attorney_user: form.attorney_mode === "user" ? form.attorney_user || null : null,
      attorney_name: form.attorney_mode === "external" ? form.attorney_name : "",
      number: form.number.trim(),
      scope: form.scope,
      eop_client: form.eop_client || null,
      eop_branch: form.eop_branch || null,
      project: form.project || null,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      notes: form.notes,
      is_active: form.is_active,
    } as Partial<PowerOfAttorneyRow>;
    if (editingId) {
      const id = editingId;
      m.update.mutate(
        { id, payload },
        {
          onSuccess: () => {
            if (file) m.uploadFile.mutate({ id, file });
            closeForm();
          },
        },
      );
    } else {
      m.create.mutate(payload, {
        onSuccess: (poa) => {
          if (file) m.uploadFile.mutate({ id: poa.id, file });
          closeForm();
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            Rejestr pełnomocnictw <ShieldCheck size={20} />
          </span>
        }
        subtitle="Mocodawca → pełnomocnik, skan, ważność — auto-podpinane do wniosków urzędowych"
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <SectionCard
          className="xl:col-span-2"
          title={
            <span className="flex items-center gap-1">
              Pełnomocnictwa
              <InfoHint text="Każdy wpis = jedno udzielone pełnomocnictwo z oknem ważności (puste „do” = bezterminowe). Statusy: Aktywne / Wygasa wkrótce (<30 dni) / Wygasłe. Silnik wniosków sam wybiera NAJDŁUŻEJ ważne pasujące pełnomocnictwo dla osoby odpowiedzialnej i ostrzega, gdy wygaśnie przed terminem KPA sprawy. Pełnomocnik spoza systemu (np. pracownik klienta) — wpisz imię i nazwisko tekstem." />
            </span>
          }
          action={<Button size="sm" onClick={openCreate}>+ Dodaj</Button>}
        >
          {isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="block">
                  <span className="section-label">Mocodawca</span>
                  <input
                    className="input-base mt-1" value={filterPrincipal} placeholder="szukaj…"
                    onChange={(e) => setFilterPrincipal(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="section-label">Pełnomocnik</span>
                  <input
                    className="input-base mt-1" value={filterAttorney} placeholder="szukaj…"
                    onChange={(e) => setFilterAttorney(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="section-label">Status</span>
                  <select
                    className="input-base mt-1" value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as PoaValidity | "")}
                  >
                    <option value="">Wszystkie statusy</option>
                    {STATUS_KEYS.map((k) => (
                      <option key={k} value={k}>{VALIDITY_BADGE[k].label}</option>
                    ))}
                  </select>
                </label>
              </div>
              {!rows.length ? (
                <EmptyState>{hasFilters ? "Brak pełnomocnictw spełniających kryteria" : "Brak pełnomocnictw"}</EmptyState>
              ) : (
                <TableShell>
                  <thead>
                    <tr>
                      <Th>Mocodawca</Th><Th>Pełnomocnik</Th><Th>Nr</Th>
                      <Th>
                        <button
                          type="button" className="uppercase tracking-widest hover:text-[var(--color-text)]"
                          onClick={() => toggleSort("validity")}
                        >
                          Ważność{sortArrow("validity")}
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button" className="uppercase tracking-widest hover:text-[var(--color-text)]"
                          onClick={() => toggleSort("status")}
                        >
                          Status{sortArrow("status")}
                        </button>
                      </Th>
                      <Th>Skan</Th><Th> </Th>
                    </tr>
                  </thead>
              <tbody>
                {rows.map((p) => {
                  const badge = VALIDITY_BADGE[p.validity];
                  return (
                    <tr key={p.id} className="border-t border-[var(--color-border)]">
                      <td className="px-2 py-1.5">
                        <div>{p.principal_display}</div>
                        {(p.eop_branch_name || p.eop_client_name || p.project_name) && (
                          <div className="text-xs text-[var(--color-text-muted)]">
                            {[p.eop_branch_name || p.eop_client_name, p.project_name]
                              .filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">{p.attorney_display}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">
                        {p.number ? (
                          p.number
                        ) : p.number_required ? (
                          <span className="text-[var(--color-primary)]" title="TAURON drukuje numer na porozumieniu — bez niego generacja stanie">
                            brak numeru
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs">
                        {p.valid_from ? fmtDate(p.valid_from) : "—"} → {p.valid_until ? fmtDate(p.valid_until) : "bezterminowe"}
                      </td>
                      <td className="px-2 py-1.5"><Badge color={badge.color}>{badge.label}</Badge></td>
                      <td className="px-2 py-1.5">
                        {p.file?.url ? (
                          <a href={p.file.url} download className="font-mono text-xs hover:underline">PDF</a>
                        ) : (
                          <label className="cursor-pointer text-xs text-[var(--color-primary)] hover:underline">
                            <Upload size={11} className="inline mr-0.5" />wgraj
                            <input
                              type="file" className="hidden" accept=".pdf"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) m.uploadFile.mutate({ id: p.id, file: f });
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          type="button"
                          className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 text-xs"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil size={12} /> Edytuj
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                </TableShell>
              )}
            </>
          )}
        </SectionCard>

        <div className="space-y-4">
          <SectionCard
            title={
              <span className="flex items-center gap-1">
                Konfiguracja mocodawcy
                <InfoHint text="Per klient: URZĄD OPŁATY SKARBOWEJ — urząd miasta siedziby mocodawcy, czyli podmiotu z pola 1 wniosku; 17 zł za każde pełnomocnictwo idzie ZAWSZE tam, nigdy do urzędu-adresata wniosku (pole 3). Bez wpisu system sam rozpozna urząd po mieście siedziby wnioskodawcy, a w oknie opłaty można ten wybór poprawić ręcznie." />
              </span>
            }
          >
            <div className="space-y-3 text-xs">
              <label className="block">
                <span className="section-label">Klient</span>
                <SearchableSelect
                  options={clientOptions}
                  value={configClient}
                  placeholder="Wybierz klienta…"
                  onChange={(e) => setConfigClient(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="section-label">Urząd opłaty skarbowej (siedziba inwestora)</span>
                <SearchableSelect
                  options={(offices ?? []).map((o) => ({ value: o.id, label: o.name }))}
                  value={configOffice}
                  placeholder="Wybierz urząd…"
                  onChange={(e) => setConfigOffice(e.target.value)}
                />
              </label>
              <Button
                size="sm"
                disabled={!configClient}
                loading={m.setClientConfig.isPending}
                onClick={() =>
                  m.setClientConfig.mutate({
                    client: configClient,
                    stamp_duty_office: configOffice || undefined,
                  })
                }
              >
                Zapisz konfigurację
              </Button>
              {!!savedConfigs?.stamp_duty.length && (
                <div className="pt-2 border-t border-[var(--color-border)] space-y-1">
                  <span className="section-label">Zapisane</span>
                  {savedConfigs.stamp_duty.map((r) => (
                    <button
                      key={r.client_id ?? "elektim"}
                      type="button"
                      className="block w-full text-left hover:underline disabled:no-underline"
                      disabled={!r.client_id}
                      onClick={() => {
                        setConfigClient(r.client_id!);
                        setConfigOffice(r.office_id);
                      }}
                    >
                      {r.principal_name} → {r.office_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            title={
              <span className="flex items-center gap-1">
                Konfiguracja urzędu (adresata)
                <InfoHint text="Per urząd, do którego trafia wniosek: niektóre urzędy honorują jedno pełnomocnictwo (klient → ELEKTIM), inne wymagają pełnego łańcucha z drugim ogniwem (zarząd klienta → pracownik klienta) — wtedy każdy wniosek do tego urzędu niesie 2 pełnomocnictwa i 2 opłaty." />
              </span>
            }
          >
            <div className="space-y-3 text-xs">
              <label className="block">
                <span className="section-label">Urząd (adresat wniosków)</span>
                <SearchableSelect
                  options={(offices ?? []).map((o) => ({ value: o.id, label: o.name }))}
                  value={chainOffice}
                  placeholder="Wybierz urząd…"
                  onChange={(e) => setChainOffice(e.target.value)}
                />
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={chainSecond} onChange={() => setChainSecond((v) => !v)} />
                Wymaga drugiego pełnomocnictwa (zarząd → pracownik klienta)
              </label>
              <Button
                size="sm"
                disabled={!chainOffice}
                loading={m.setOfficeConfig.isPending}
                onClick={() =>
                  m.setOfficeConfig.mutate({
                    office: chainOffice,
                    requires_second_poa: chainSecond,
                  })
                }
              >
                Zapisz konfigurację
              </Button>
              {!!savedConfigs?.chains.length && (
                <div className="pt-2 border-t border-[var(--color-border)] space-y-1">
                  <span className="section-label">Zapisane</span>
                  {savedConfigs.chains.map((r) => (
                    <button
                      key={r.office_id}
                      type="button"
                      className="flex w-full items-center justify-between gap-2 text-left hover:underline"
                      onClick={() => setChainOffice(r.office_id)}
                    >
                      <span>{r.office_name}</span>
                      <Badge color={r.requires_second_poa ? "yellow" : "gray"}>
                        {r.requires_second_poa ? "2 pełnomocnictwa" : "1 wystarczy"}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {formOpen && (
        <Modal
          open
          onClose={closeForm}
          title={editingId ? "Edytuj pełnomocnictwo" : "Nowe pełnomocnictwo"}
          size="lg"
        >
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Mocodawca</span>
                <select className="input-base mt-1" value={form.principal_kind}
                        onChange={(e) => set("principal_kind", e.target.value as "client" | "elektim")}>
                  <option value="client">Klient (inwestor)</option>
                  <option value="elektim">GK ELEKTIM</option>
                </select>
              </label>
              {form.principal_kind === "client" && (
                <label className="block">
                  <span className="section-label">Klient *</span>
                  <SearchableSelect
                    options={clientOptions}
                    value={form.principal_client}
                    placeholder="Wybierz klienta…"
                    onChange={(e) => set("principal_client", e.target.value)}
                  />
                </label>
              )}
            </div>
            <label className="block">
              <span className="section-label flex items-center gap-1">
                Mocodawca — doprecyzowanie
                <InfoHint text="Np. „Zarząd X S.A.” dla drugiego ogniwa łańcucha (zarząd → pracownik klienta). Puste = nazwa klienta / ELEKTIM." />
              </span>
              <input className="input-base mt-1" value={form.principal_label}
                     onChange={(e) => set("principal_label", e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Klient (oddziału)
                  <InfoHint text="Opcjonalne — klient (operator), którego oddziału dotyczy pełnomocnictwo. Można wskazać sam klienta, bez oddziału." />
                </span>
                <SearchableSelect
                  options={clientOptions}
                  value={form.eop_client}
                  placeholder="— brak —"
                  onChange={(e) => { set("eop_client", e.target.value); set("eop_branch", ""); }}
                />
              </label>
              <label className="block">
                <span className="section-label flex items-center gap-1">
                  Oddział
                  <InfoHint text="Oddział, którego dotyczy pełnomocnictwo. Po nim automat dobiera pełnomocnictwo do wniosku — bez oddziału wpis jest traktowany jako uniwersalny (może trafić na wniosek dowolnego oddziału)." />
                </span>
                <SearchableSelect
                  options={branchOptions}
                  value={form.eop_branch}
                  placeholder={branchClientId ? "— brak —" : "najpierw wybierz klienta"}
                  disabled={!branchClientId}
                  onChange={(e) => set("eop_branch", e.target.value)}
                />
              </label>
            </div>
            <label className="block">
              <span className="section-label flex items-center gap-1">
                Przedsięwzięcie{isTauron ? " *" : ""}
                <InfoHint text="Opcjonalne — przedsięwzięcie, którego dotyczy pełnomocnictwo. Dla TAURONA obowiązkowe: pełnomocnictwo jest wystawiane osobno na każde zlecenie." />
              </span>
              <SearchableSelect
                options={projectOptions}
                value={form.project}
                placeholder="— brak —"
                onChange={(e) => set("project", e.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Pełnomocnik</span>
                <select className="input-base mt-1" value={form.attorney_mode}
                        onChange={(e) => set("attorney_mode", e.target.value as "user" | "external")}>
                  <option value="user">Pracownik (z systemu)</option>
                  <option value="external">Osoba spoza systemu</option>
                </select>
              </label>
              {form.attorney_mode === "user" ? (
                <label className="block">
                  <span className="section-label">Pracownik *</span>
                  <SearchableSelect
                    options={userOptions}
                    value={form.attorney_user}
                    placeholder="Wybierz osobę…"
                    onChange={(e) => set("attorney_user", e.target.value)}
                  />
                </label>
              ) : (
                <label className="block">
                  <span className="section-label">Imię i nazwisko *</span>
                  <input className="input-base mt-1" value={form.attorney_name}
                         onChange={(e) => set("attorney_name", e.target.value)} />
                </label>
              )}
            </div>
            <label className="block">
              <span className="section-label flex items-center gap-1">
                Nr pełnomocnictwa{isTauron ? " *" : ""}
                <InfoHint text="Numer nadany przez mocodawcę. TAURON drukuje go na porozumieniu („legitymujący się pełnomocnictwem nr …”) i numeruje pełnomocnictwa osobno dla każdego zlecenia — bez numeru generacja porozumień na tym zleceniu jest blokowana. ENERGA numeru nie używa." />
              </span>
              <input className="input-base mt-1" value={form.number}
                     placeholder={isTauron ? "np. 123/2026" : "opcjonalnie"}
                     onChange={(e) => set("number", e.target.value)} />
            </label>
            <label className="block">
              <span className="section-label">Zakres umocowania</span>
              <textarea className="input-base mt-1 h-14" value={form.scope}
                        onChange={(e) => set("scope", e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Ważne od</span>
                <DatePicker className="mt-1" value={form.valid_from}
                       onChange={(v) => set("valid_from", v)} />
              </label>
              <label className="block">
                <span className="section-label">Ważne do (puste = bezterminowe)</span>
                <DatePicker className="mt-1" value={form.valid_until}
                       onChange={(v) => set("valid_until", v)} />
              </label>
            </div>
            <div>
              <span className="section-label">
                {editingId ? "Skan pełnomocnictwa (PDF) — wgraj, aby wymienić" : "Skan pełnomocnictwa (PDF)"}
              </span>
              <FileDropZone className="mt-1" accept=".pdf"
                            files={file ? [file] : []}
                            onFilesChange={(fs) => setFile(fs[0] ?? null)} />
              {editingId && !file && (
                <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
                  {editingRow?.file?.url ? "Aktualny skan zostanie zachowany." : "Brak wgranego skanu."}
                </span>
              )}
            </div>
            {editingId && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                       onChange={(e) => set("is_active", e.target.checked)} />
                <span className="flex items-center gap-1">
                  Aktywne
                  <InfoHint text="Odznacz, aby wycofać pełnomocnictwo — nieaktywne wpisy nie są auto-podpinane do wniosków urzędowych." />
                </span>
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeForm}>Anuluj</Button>
              <Button
                onClick={submit}
                loading={editingId ? m.update.isPending : m.create.isPending}
                disabled={
                  (form.principal_kind === "client" && !form.principal_client)
                  || (form.attorney_mode === "user" ? !form.attorney_user : !form.attorney_name)
                  // TAURON: numer + zlecenie obowiązkowe (numer drukuje się na porozumieniu).
                  || (isTauron && (!form.number.trim() || !form.project))
                }
              >
                {editingId ? "Zapisz zmiany" : "Dodaj pełnomocnictwo"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
